import { action, computed, observable } from 'mobx';
import type { App } from '../../app';
import { Api } from '../../api';
import { type Question } from '../types';
import { type LastPageButtonProps, LastPageModel } from '../LastPageView';
import { type QuestionModel, ValidationError } from './QuestionModel';

import {
  getSuitableRelation,
  calcSurveyProgress,
  findStartQuestion,
  createQuestionModel,
  getNextQuestion,
  createUserAnswerToSend
} from './helpers';

/**
 * Управление опросом (переход к следующему вопросу, рассчет прогресса, ...)
 */
export class Survey {
  private app: App;
  cmId: string; // ID участника опроса (поле crm_cm_id в таблице survey.xs_cm)
  formVersionId: number;
  private questions: Question[];
  questionHistory: QuestionModel[] = [];
  @observable.ref accessor currentQuestionModel: QuestionModel;
  maxProgressStepsCount?: number;
  @observable private accessor answeredQuestionCount: number = 0;
  @observable accessor sendingUserAnswer: boolean = false;
  @observable accessor finished: boolean = false;
  defaultLastPageButtonProps: LastPageButtonProps;

  @action
  private set_sendingUserAnswer(sendingUserAnswer: boolean) {
    this.sendingUserAnswer = sendingUserAnswer;
  }

  @action
  private set_finished(finished: boolean) {
    this.finished = finished;
  }

  @action
  private set_currentQuestionModel(currentQuestionModel: QuestionModel) {
    this.currentQuestionModel = currentQuestionModel;
  }

  @action
  private set_answeredQuestionCount(answeredQuestionCount: number) {
    this.answeredQuestionCount = answeredQuestionCount;
  }

  constructor(
    app: App,
    cmId: string,
    formVersionId: number,
    questions: Question[],
    defaultLastPageButtonProps: LastPageButtonProps,
    maxProgressStepsCount?: number
  ) {
    this.app = app;
    this.cmId = cmId;
    this.formVersionId = formVersionId;
    this.questions = questions;
    this.maxProgressStepsCount = maxProgressStepsCount;
    this.defaultLastPageButtonProps = defaultLastPageButtonProps;
    this.currentQuestionModel = createQuestionModel(this, findStartQuestion(this.questions));
    this.questionHistory.push(this.currentQuestionModel);
  }

  @computed
  get progress() {
    return calcSurveyProgress(this.answeredQuestionCount, this.finished, this.maxProgressStepsCount);
  }

  @action
  goToNextQuestion = async () => {
    const prevQuestionModel = this.currentQuestionModel;

    try {
      this.currentQuestionModel.validateAnswers();

      const suitableRelation = getSuitableRelation(this.currentQuestionModel, this.questionHistory);
      const nextQuestion = getNextQuestion(this.questions, suitableRelation);
      const nextQuestionModel = createQuestionModel(this, nextQuestion, suitableRelation);

      this.set_sendingUserAnswer(true);
      this.currentQuestionModel.set_disabled(true);

      await Api.sendUserAnswer(createUserAnswerToSend(this.formVersionId, this.cmId, this.currentQuestionModel, nextQuestionModel));

      if (nextQuestionModel instanceof LastPageModel) {
        this.set_finished(true);
      }

      this.set_currentQuestionModel(nextQuestionModel);
      this.questionHistory.push(this.currentQuestionModel);
      this.set_answeredQuestionCount(this.answeredQuestionCount + 1);
    } catch (err) {
      if (err instanceof ValidationError) {
        return;
      }

      this.app.set_error(err);
    } finally {
      this.set_sendingUserAnswer(false);
      prevQuestionModel.set_disabled(false);
    }
  };
}
